import fetchApi from '../util/fetchApi';
import { SESSION_HEADER, SessionAuthenticateResponse } from '../api/AuthApi';

/**
 * Client side of the cookie session flow.
 *
 * The backend keeps the long-lived refresh token in an HttpOnly cookie that script can never read; this
 * module holds only the short-lived (15 min) access token, in memory. Nothing auth-related is written to
 * localStorage — a page reload gets a new access token from `security/session/refresh`.
 *
 * Tabs: every tab of the app shares the one cookie (so one server session) but has its own memory.
 * - A refresh rotates the cookie, so two tabs refreshing at once would present the same cookie twice —
 *   which the server treats as theft once its grace period lapses. Refreshes are therefore serialized
 *   across tabs with the Web Locks API, and a tab that waited on the lock skips its own refresh when
 *   another tab refreshed in the meantime.
 * - Every new session state (login, refresh, impersonation) and every logout is sent to the other tabs
 *   over a BroadcastChannel, so they switch identity or log out together.
 */

/** Why the server ended a session, when it says (the `reason` of a session/refresh 401). */
export type SessionEndReason = 'idle-timeout';

export type SessionEvent =
  | { type: 'session'; response: SessionAuthenticateResponse }
  /** A renewed token for the session; listeners may skip work when who is logged in did not change. */
  | { type: 'session-refresh'; response: SessionAuthenticateResponse }
  | { type: 'ended'; reason?: SessionEndReason };

type Listener = (event: SessionEvent) => void;
type SessionUpdateType = 'session' | 'session-refresh';

const CHANNEL_NAME = 'uwe-session';
const LOCK_NAME = 'uwe-session-refresh';
/** Refresh once this fraction of the access token's lifetime has elapsed. */
const REFRESH_AT_FRACTION = 0.8;
/** Treat a token this close to expiry as already expired (clock skew, request latency). */
const EXPIRY_MARGIN_MS = 30_000;
/** Ask the server this long after the idle deadline, so its clock has certainly passed it too. */
const IDLE_CHECK_DELAY_MS = 5_000;

let accessToken: string | null = null;
let currentResponse: SessionAuthenticateResponse | null = null;
/** Bumped whenever this tab learns of a new session state, from its own request or another tab. A counter,
 * not a timestamp: a broadcast landing in the same millisecond as a refresh request must still count. */
let sessionGeneration = 0;
let inflight: Promise<SessionAuthenticateResponse | null> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<Listener>();

const channel: BroadcastChannel | null = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

/** The current access token, or null when there is no session. Read on every API request. */
export function getToken(): string | null {
  return accessToken;
}

/** The most recent session response (user, impersonator) this tab knows of. */
export function getCurrentSession(): SessionAuthenticateResponse | null {
  return currentResponse;
}

export function onSessionEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(event: SessionEvent) {
  listeners.forEach(listener => listener(event));
}

function adopt(response: SessionAuthenticateResponse, type: SessionUpdateType) {
  accessToken = response.token;
  currentResponse = response;
  sessionGeneration++;
  scheduleRefresh(response.token);
  if (response.idleExpiresInSeconds != null) scheduleIdleCheck(response.idleExpiresInSeconds);
  emit({ type, response });
}

function drop(reason?: SessionEndReason) {
  accessToken = null;
  currentResponse = null;
  sessionGeneration++;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = null;
  emit({ type: 'ended', reason });
}

/**
 * Makes `response` the current session in this tab and every other tab. Call it with whatever a login,
 * impersonation or user refresh returned; `'session-refresh'` when it only renewed the current session's token.
 */
export function publishSession(response: SessionAuthenticateResponse, type: SessionUpdateType = 'session') {
  adopt(response, type);
  channel?.postMessage({ type, response } satisfies SessionEvent);
}

/**
 * Swaps in a replacement access token for the current session without changing who is logged in — e.g.
 * after a password change, which revoked the token that made the request.
 */
export function replaceAccessToken(token: string) {
  if (!currentResponse) return;
  // The idle countdown in currentResponse is as old as the response; leave the running idle check alone.
  publishSession(
    { ...currentResponse, token, idleExpiresAt: undefined, idleExpiresInSeconds: undefined },
    'session-refresh',
  );
}

/** Ends the session in this tab and every other tab, without contacting the server. */
export function clearSession(reason?: SessionEndReason) {
  drop(reason);
  channel?.postMessage({ type: 'ended', reason } satisfies SessionEvent);
}

channel?.addEventListener('message', (message: MessageEvent<SessionEvent>) => {
  // Another tab's news: adopt it here, but never re-broadcast (every tab would echo forever).
  if (message.data?.type === 'session' || message.data?.type === 'session-refresh') {
    adopt(message.data.response, message.data.type);
  } else if (message.data?.type === 'ended') drop(message.data.reason);
});

function withLock<T>(fn: () => Promise<T>): Promise<T> {
  // Web Locks need a secure context (https, or http://localhost). Without them the server's rotation
  // grace period still absorbs concurrent refreshes; this only makes them rarer.
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  // lib.dom types request() as Promise<ReturnType<callback>>, i.e. Promise<Promise<T>>; at runtime the
  // callback's promise is adopted, so it resolves to T.
  return locks ? (locks.request(LOCK_NAME, fn) as unknown as Promise<T>) : fn();
}

/**
 * Exchanges the session cookie for a new access token. Concurrent calls in this tab share one request;
 * across tabs they queue on a lock and reuse another tab's fresh result. Resolves to the new session, or
 * null when there is none — after a 401 the session is ended everywhere; after a network error it is
 * left alone (being offline must not log anyone out).
 */
export function refreshSession(): Promise<SessionAuthenticateResponse | null> {
  if (inflight) return inflight;

  const generationAtRequest = sessionGeneration;
  const hadSession = currentResponse != null;
  inflight = withLock(async () => {
    if (sessionGeneration !== generationAtRequest) return currentResponse;

    let fetchResponse: Response;
    try {
      fetchResponse = await fetchApi.api.post('security/session/refresh', {
        json: false,
        credentials: 'include',
        headers: SESSION_HEADER,
        onUnauthorized: undefined,
      });
    } catch {
      return null;
    }

    if (fetchResponse.status === 401) {
      const reason = await readEndReason(fetchResponse);
      // Only a tab that had a session may end it everywhere. A tab starting up without one may simply
      // have raced another tab's login (asking before the cookie existed) and must not log that tab out.
      if (hadSession) clearSession(reason);
      else drop(reason);
      return null;
    }
    if (!fetchResponse.ok) return null;

    const body = await fetchResponse.json();
    // Even a page load's first refresh: listeners tell from their own state whether anyone changed.
    publishSession(body.data as SessionAuthenticateResponse, 'session-refresh');
    return currentResponse;
  }).finally(() => {
    inflight = null;
  });

  return inflight;
}

async function readEndReason(response: Response): Promise<SessionEndReason | undefined> {
  try {
    const body = await response.json();
    return body?.reason === 'idle-timeout' ? 'idle-timeout' : undefined;
  } catch {
    return undefined;
  }
}

/** For fetchApi's `onUnauthorized`: a fresh access token after a 401, or nothing. */
export async function refreshAccessToken(): Promise<string | null> {
  return (await refreshSession())?.token ?? null;
}

/** Logs out on the server (revoking the session, clearing the cookie) and in every tab. */
export async function endSession(): Promise<void> {
  try {
    await fetchApi.api.post('security/session/logout', {
      json: false,
      credentials: 'include',
      headers: SESSION_HEADER,
      onUnauthorized: undefined,
    });
  } catch {
    // Still log out locally: the cookie expires on its own, and the user asked to leave.
  } finally {
    clearSession();
  }
}

/** Seconds-since-epoch `iat`/`exp` from a JWT's payload, without verifying it (the server does that). */
export function readTokenTimes(token: string): { issuedAt?: number; expiresAt?: number } {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { iat, exp } = JSON.parse(atob(payload));
    return { issuedAt: iat, expiresAt: exp };
  } catch {
    return {};
  }
}

function scheduleRefresh(token: string) {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;

  const { issuedAt, expiresAt } = readTokenTimes(token);
  if (!expiresAt) return;

  const expiresAtMs = expiresAt * 1000;
  const refreshAtMs = issuedAt
    ? issuedAt * 1000 + (expiresAtMs - issuedAt * 1000) * REFRESH_AT_FRACTION
    : expiresAtMs - 2 * EXPIRY_MARGIN_MS;
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    refreshSession();
  }, Math.max(0, refreshAtMs - Date.now()));
}

/**
 * Pushes this tab's idle check out to a new deadline, e.g. after `security/session/activity` reported
 * user activity. Ignored without a session.
 */
export function extendIdleDeadline(secondsUntilIdle: number) {
  if (currentResponse) scheduleIdleCheck(secondsUntilIdle);
}

/**
 * At the idle deadline, asks the server whether the session is still live rather than logging out on
 * this tab's word: activity in another tab may have extended it. The server answers a refresh past the
 * deadline with a 401, which ends the session in every tab; otherwise the new session carries the new
 * deadline, which reschedules this check.
 */
function scheduleIdleCheck(secondsUntilIdle: number) {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    idleTimer = null;
    refreshSession();
  }, Math.max(0, secondsUntilIdle * 1000) + IDLE_CHECK_DELAY_MS);
}

function isExpiringSoon(token: string | null): boolean {
  if (!token) return true;
  const { expiresAt } = readTokenTimes(token);
  return !expiresAt || expiresAt * 1000 - Date.now() < EXPIRY_MARGIN_MS;
}

// Browsers throttle timers in background tabs, so a tab coming back may hold an expired token. Renew it
// before the user's first click hits a 401. Only for a tab that had a session: a logged-out tab stays out.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && currentResponse && isExpiringSoon(accessToken)) {
      refreshSession();
    }
  });
}

/** Test hook: forget all module state. */
export function __resetForTests() {
  accessToken = null;
  currentResponse = null;
  sessionGeneration = 0;
  inflight = null;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = null;
  listeners.clear();
}
